/**
 * BandejaPrenomina.js
 *
 * La pantalla de entrada de PreNómina: un selector de periodo, un botón, la lista de
 * nóminas recientes y la entrada al importador de nómina histórica. Nada más.
 *
 * Reemplaza al select plano que listaba TODOS los periodos de la empresa, ordenados por
 * un `numero` que se reinicia cada año, sin decir qué estaba timbrado y sin mostrar la
 * preselección (nunca se le pasaba `value`).
 *
 * Las opciones van agrupadas por tiempo —Pasados / En curso / Próximos— y coloreadas:
 * en una lista de 50+ periodos lo que hace falta es ubicarse de un vistazo, sin leer.
 * El nombre de la cadencia va al frente de cada opción porque todas las cadencias se
 * muestran mezcladas, y `PQ2026-17` y `PS2026-17` son fechas totalmente distintas.
 *
 * El periodo que llega preseleccionado lo decide el backend (ver
 * Controllers/Prenomina/bandeja.controllers.js): el más cercano a hoy, admitiendo el
 * anterior mientras esté dentro de la ventana de gracia —5 días por omisión, ajustable
 * por empresa—, saltando los que ya están timbrados o parcialmente timbrados, y sólo
 * entre las cadencias que la empresa dejó compitiendo. Las cadencias descartadas siguen
 * apareciendo completas en la lista: lo único que pierden es proponerse solas.
 *
 * No navega sola al entrar —salvo que la empresa haya activado "entrar directo", y aun
 * entonces la decisión la toma el padre, no esta pantalla: tiene que sobrevivir al
 * remonte con el que se regresa aquí. El padre (PreNomina.js) conserva la creación y la
 * carga; este componente pide acciones por callback y nunca toca `history`.
 */

import React, { Component } from 'react'
import { Row, Col, Select, Tag, Skeleton, Empty, Alert, Table, Tooltip } from 'antd'
import { HistoryOutlined, SyncOutlined, SettingOutlined } from '@ant-design/icons'
import axios from 'axios'
import { connect } from 'react-redux'

import { ButtonItem, SelectItem, dineroDisabledMask } from '../../../../Components/Items'
import { momentDeFechaDia } from '../../../../Components/fmtFechaDia'
import { colorEstatus, nombreEstatus, etiquetaEstadoPeriodo } from '../estatusPrenomina'

const { Option, OptGroup } = Select

// El aire de arriba va como PADDING del contenedor, no como margen del primer hijo:
// la pantalla se monta dentro del <Spin> de PreNomina, y ese contenedor —igual que el
// panel blanco de afuera— no tiene padding superior, así que un margin-top del primer
// hijo se colapsa hacia afuera y empuja el panel entero en vez de separar por dentro.
//
// Sólo vertical: los 20px laterales ya los pone el contenedor blanco de PreNomina.
const ENVOLTURA = { paddingTop: 20, paddingBottom: 4 }

const ZONA = { maxWidth: 640, margin: '0 auto' }

// La tarjeta del selector: es lo primero que hay que hacer al entrar, así que se
// despega del fondo con el color de marca en vez de quedar como un par de campos
// sueltos. La barra superior es lo que lo señala sin gritar.
const TARJETA = {
  background: '#fff',
  border: '1px solid #c7f0f6',
  borderTop: '3px solid #00BFD6',
  borderRadius: 10,
  padding: '18px 22px 20px',
  boxShadow: '0 2px 12px rgba(0,191,214,0.10)',
}

const TITULO_TARJETA = {
  fontSize: 16, fontWeight: 600, color: '#17323a', marginBottom: 2,
}

// Los encabezados secundarios (recientes) van deliberadamente más discretos que el
// título de la tarjeta, para que la jerarquía se lea sola.
const TITULO_SECCION = {
  fontSize: 12, color: '#8c8c8c', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8,
}

const ETIQUETA_TENUE = { fontSize: 12, color: '#888' }

const BOX_AVISO = {
  background: '#fffbe6', border: '1px solid #ffe58f', color: '#7c5a00',
  borderRadius: 6, padding: '8px 10px', fontSize: 12,
}

// Cada periodo cae en uno de tres tiempos, y cada uno tiene su color. El color va en el
// TEXTO del renglón, no sólo en un tag al final.
const TIEMPO = {
  pasado: { titulo: 'Pasados', color: '#8c8c8c', fondo: '#fafafa', barra: '#bfbfbf' },
  curso: { titulo: 'En curso', color: '#00879a', fondo: '#e6feff', barra: '#00BFD6' },
  futuro: { titulo: 'Próximos', color: '#389e0d', fondo: '#f6ffed', barra: '#52c41a' },
}

const ORDEN_TIEMPO = ['pasado', 'curso', 'futuro']

const tiempoDePeriodo = (p) => (p.enCurso ? 'curso' : p.yaTermino ? 'pasado' : 'futuro')

// Las fechas de Periodo son fechas-día: los renglones nuevos vienen anclados al
// mediodía UTC, pero los capturados a mano no. momentDeFechaDia lee bien las dos.
const dia = (f) => {
  const m = momentDeFechaDia(f)
  return m ? m.format('DD/MM') : '—'
}

class BandejaPrenomina extends Component {
  state = {
    cargando: true,
    errorCarga: null,
    periodos: null,     // null = nunca cargó; [] = ese ejercicio no tiene periodos
    ejercicios: [],
    ejercicio: null,
    periodoId: null,    // el value del select
    recientes: [],
    actuando: false,
  }

  // El padre desmonta este componente al navegar a la prenómina, y eso pasa en medio
  // de los `await` de abajo.
  _montado = false
  // El año se cambia más rápido que el viaje redondo: sin token, una respuesta lenta
  // puede pintarse encima de la lista del año que el usuario ya eligió.
  _req = 0

  componentDidMount() {
    this._montado = true
    this.consultar()
  }

  componentWillUnmount() {
    this._montado = false
  }

  // ── Carga ──────────────────────────────────────────────────────────────────
  // Sin `ejercicio` el backend calcula la preselección y devuelve la lista del año al
  // que ésta pertenece. Con `ejercicio` devuelve ese año sin preseleccionar nada.
  //
  // Ojo: la llave se OMITE en la primera carga. Mandar `ejercicio: null` significaría
  // el bucket "Sin ejercicio" (periodos capturados a mano sin año), no "sin filtro".
  async consultar(ejercicio) {
    const primera = ejercicio === undefined
    const req = ++this._req
    this.setState({ cargando: true, errorCarga: null })

    const { empresaId, headersToken } = this.props
    try {
      // La lista de recientes es accesoria: si falla se queda vacía, pero el selector
      // —que es para lo que se entra a la pantalla— tiene que pintarse igual. Por eso
      // su error se absorbe aquí y no viaja al Promise.all. Sólo se pide la primera vez.
      const [res, resRecientes] = await Promise.all([
        axios.post(
          'prenomina/bandejaPrenomina',
          { empresaId, ...(primera ? {} : { ejercicio }) },
          { headers: headersToken }
        ),
        primera
          ? axios
            .post('prenomina/todasPrenominas', { empresaId, limit: 8 }, { headers: headersToken })
            .catch(() => null)
          : Promise.resolve(null),
      ])
      if (req !== this._req || !this._montado) return

      if (res.data.success !== 2) {
        this.setState({
          cargando: false,
          errorCarga: res.data.message || 'El servidor no devolvió los periodos.',
        })
        return
      }

      this.setState((prev) => ({
        cargando: false,
        periodos: res.data.periodos || [],
        ejercicios: res.data.ejercicios || [],
        ejercicio: res.data.ejercicio ?? null,
        periodoId: res.data.sugeridoId || null,
        recientes: primera
          ? (resRecientes?.data?.success === 2 ? (resRecientes.data.prenominas || []) : [])
          : prev.recientes,
      }))

      // Sólo en la primera carga: es la única que trae preselección. El padre decide con
      // esto si entra directo —la decisión vive allá porque tiene que sobrevivir al
      // remonte de esta pantalla— y con qué vista abre la tabla. Va después del guard de
      // carrera de arriba para no reportar desde una respuesta vieja.
      if (primera) {
        this.props.onDatosIniciales?.({
          sugeridoId: res.data.sugeridoId || null,
          preferencias: res.data.preferencias || null,
        })
      }
    } catch {
      if (req !== this._req || !this._montado) return
      this.setState({ cargando: false, errorCarga: 'No se pudo contactar al servidor.' })
    }
  }

  // Candado de acción: sólo gira el botón que se apretó, el resto se deshabilita.
  async accion(clave, fn) {
    if (this.state.actuando) return
    this.setState({ actuando: clave })
    try {
      await fn()
    } finally {
      if (this._montado) this.setState({ actuando: false })
    }
  }

  periodoSeleccionado() {
    const { periodos, periodoId } = this.state
    if (!periodoId || !periodos) return null
    return periodos.find((p) => String(p._id) === String(periodoId)) || null
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  renderSelector() {
    const { periodos, ejercicios, ejercicio, periodoId, cargando, actuando } = this.state
    const sel = this.periodoSeleccionado()

    // El texto buscable va en `label` (string) y el Tag en los children. El
    // filterOption por defecto de SelectItem llama children.toLowerCase(), que con
    // children JSX TRUENA — no sólo deja de casar. Por eso va uno explícito.
    const textoPlano = (p) =>
      `${p.cadencia} · ${p.etiqueta} · ${dia(p.fechaInicio)} al ${dia(p.fechaFin)}`

    // Los periodos llegan en orden cronológico ascendente y así se quedan; lo único
    // que se agrega es el corte por tiempo, para ubicar dónde queda hoy.
    const grupos = ORDEN_TIEMPO
      .map((clave) => ({ clave, periodos: (periodos || []).filter((p) => tiempoDePeriodo(p) === clave) }))
      .filter((g) => g.periodos.length)

    return (
      <div style={{ ...ZONA, ...TARJETA }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={TITULO_TARJETA}>¿Qué nómina vas a trabajar?</div>
            <div style={{ ...ETIQUETA_TENUE, marginBottom: 16 }}>
              Viene propuesto el periodo que toca. Cámbialo si necesitas otro.
            </div>
          </div>
          {/* Atajo a las preferencias: qué cadencias se proponen, con cuántos días de
              gracia y con qué columnas nacen las nóminas. Se ajusta aquí, que es donde
              se nota, sin ir a Configuración Nómina. */}
          {this.props.onAbrirPreferencias && (
            <Tooltip title="Preferencias de nómina">
              <ButtonItem
                blanco
                lessTime
                icon={<SettingOutlined />}
                onClick={this.props.onAbrirPreferencias}
              />
            </Tooltip>
          )}
        </div>

        <Row gutter={12}>
          <Col xs={15} sm={18}>
            <div style={{ ...ETIQUETA_TENUE, marginBottom: 4 }}>Periodo</div>
            <SelectItem
              style={{ width: '100%' }}
              placeholder={cargando ? 'Cargando periodos…' : 'Elige el periodo'}
              value={periodoId || undefined}
              loading={cargando}
              optionLabelProp="label"
              // Un NÚMERO, no `false`: rc-select apaga el scroll virtual cuando
              // dropdownMatchSelectWidth es false (generate.js:835), y con las cadencias
              // mezcladas serían ~100 nodos DOM reales en cada apertura.
              dropdownMatchSelectWidth={520}
              noFocusSiguiente
              filterOption={(input, opt) =>
                String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
              onChange={(id) => this.setState({ periodoId: id })}
            >
              {grupos.map((g) => (
                <OptGroup key={g.clave} label={TIEMPO[g.clave].titulo}>
                  {g.periodos.map((p) => {
                    const et = etiquetaEstadoPeriodo(p)
                    const t = TIEMPO[tiempoDePeriodo(p)]
                    return (
                      <Option key={p._id} value={p._id} label={textoPlano(p)}>
                        <span
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%',
                            borderLeft: `3px solid ${t.barra}`, paddingLeft: 8,
                            background: t.fondo, borderRadius: 4, color: t.color,
                            fontWeight: p.enCurso ? 600 : 400,
                          }}
                        >
                          <span style={{ flex: 1 }}>{textoPlano(p)}</span>
                          {p.enCurso && <Tag color="cyan" style={{ marginRight: 0 }}>Hoy</Tag>}
                          <Tag color={et.color} style={{ marginRight: 0 }}>{et.texto}</Tag>
                        </span>
                      </Option>
                    )
                  })}
                </OptGroup>
              ))}
            </SelectItem>
          </Col>

          <Col xs={9} sm={6}>
            <div style={{ ...ETIQUETA_TENUE, marginBottom: 4 }}>Ejercicio</div>
            <SelectItem
              style={{ width: '100%' }}
              placeholder="Año"
              value={ejercicio != null ? ejercicio : (periodos ? 'Sin ejercicio' : undefined)}
              onChange={(e) => this.consultar(e)}
            >
              {ejercicios.map((e) => (
                <Option key={String(e)} value={e == null ? 'Sin ejercicio' : e}>
                  {e == null ? 'Sin ejercicio' : e}
                </Option>
              ))}
            </SelectItem>
          </Col>
        </Row>

        {sel && (
          <div style={{ ...ETIQUETA_TENUE, marginTop: 8 }}>
            {dia(sel.fechaInicio)} al {dia(sel.fechaFin)}
            {sel.fechaPago ? ` · paga ${dia(sel.fechaPago)}` : ''}
            {sel.prenomina?.totalNeto != null
              ? ` · neto ${dineroDisabledMask(sel.prenomina.totalNeto)}`
              : ''}
          </div>
        )}

        {/* El botón manda siempre por el mismo camino, en las dos etiquetas:
            PreNomina.abrirOCrearPrenomina ya resuelve abrir-vs-crear del lado del
            servidor, con guarda contra la doble creación de dos pestañas. */}
        <ButtonItem
          cyan
          style={{ width: '100%', marginTop: 16, height: 38, fontSize: 15, fontWeight: 600 }}
          disabled={!periodoId || !!actuando || cargando}
          loading={actuando === 'ir'}
          onClick={() => this.accion('ir', () => this.props.onAbrirOCrearPeriodo(periodoId))}
        >
          {sel?.prenomina ? 'Continuar' : 'Crear'}
        </ButtonItem>

        {periodos && !periodos.length && (
          <div style={{ ...BOX_AVISO, marginTop: 10 }}>
            Este ejercicio no tiene periodos generados.{' '}
            <ButtonItem
              blanco
              style={{ width: '100%', marginTop: 8 }}
              onClick={() => this.props.onIrAPeriodos()}
            >
              Generar periodos
            </ButtonItem>
          </div>
        )}
      </div>
    )
  }

  renderRecientes() {
    const { recientes, actuando } = this.state

    // <Table> de antd y no TableItem: TableItem fuerza scroll={{y:'60vh'}} y su propia
    // paginación, así que una lista de 8 renglones saldría con un contenedor de 60vh y
    // un pie de "1-8 de 8" (mismo motivo que documenta ModalResetearLineas).
    return (
      <div style={{ ...ZONA, marginTop: 52 }}>
        <div style={TITULO_SECCION}>Nóminas recientes</div>
        <Table
          size="small"
          pagination={false}
          rowKey="_id"
          dataSource={recientes}
          locale={{ emptyText: 'Todavía no hay nóminas en esta empresa' }}
          scroll={{ x: 'max-content' }}
          onRow={(r) => ({
            style: { cursor: actuando ? 'progress' : 'pointer' },
            onClick: () => this.accion(`rec-${r._id}`, () => this.props.onAbrirPrenomina(r._id)),
          })}
          columns={[
            {
              title: 'Nómina',
              dataIndex: 'nombre',
              ellipsis: true,
              render: (v, r) => (
                <>
                  {v || r.periodoEtiqueta || r.serieNumero}
                  {r.esHistorica && <Tag color="cyan" style={{ marginLeft: 6 }}>HIST</Tag>}
                </>
              ),
            },
            {
              title: 'Periodo',
              key: 'periodo',
              width: 150,
              responsive: ['md'],
              render: (_, r) => `${dia(r.fechaInicio)} al ${dia(r.fechaFin)}`,
            },
            {
              title: 'Estatus',
              dataIndex: 'estatus',
              width: 120,
              render: (v) => <Tag color={colorEstatus(v)}>{nombreEstatus(v)}</Tag>,
            },
            {
              title: 'Neto',
              dataIndex: 'totalNeto',
              width: 120,
              align: 'right',
              render: (v) => (v == null ? '—' : dineroDisabledMask(v)),
            },
          ]}
        />
      </div>
    )
  }

  renderSkeleton() {
    return (
      <div style={ZONA}>
        <Skeleton active title={{ width: '30%' }} paragraph={{ rows: 1, width: ['100%'] }} />
        <Skeleton.Input active style={{ width: '100%', height: 30, marginTop: 12 }} />
        <div style={{ marginTop: 28 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton.Input key={i} active style={{ width: '100%', height: 18, marginBottom: 8 }} />
          ))}
        </div>
      </div>
    )
  }

  renderError() {
    return (
      <div style={ZONA}>
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los periodos"
          description={this.state.errorCarga}
          action={
            <ButtonItem blanco icon={<SyncOutlined />} onClick={() => this.consultar()}>
              Reintentar
            </ButtonItem>
          }
        />
      </div>
    )
  }

  // Cubre los dos casos que llegan aquí, porque desde el front no se distinguen: la
  // empresa sin ningún tipo de periodo dado de alta, y la que ya lo tiene pero nunca
  // generó su calendario. Los dos botones cubren los dos remedios.
  renderVacio() {
    const { onIrATiposPeriodos, onIrAPeriodos } = this.props
    return (
      <div style={{ ...ZONA, paddingTop: 24 }}>
        <Empty
          description={(
            <span>
              Esta empresa todavía no tiene periodos de nómina.
              <br />
              <span style={ETIQUETA_TENUE}>
                Primero se da de alta el tipo de periodo (semanal, quincenal…) y luego se
                generan sus periodos del ejercicio.
              </span>
            </span>
          )}
        >
          {onIrATiposPeriodos && (
            <ButtonItem cyan onClick={onIrATiposPeriodos}>Configurar tipos de periodo</ButtonItem>
          )}
          <ButtonItem blanco style={{ marginTop: 8 }} onClick={onIrAPeriodos}>
            Ir a Periodos
          </ButtonItem>
        </Empty>
      </div>
    )
  }

  // Carga de un periodo YA PAGADO en el sistema anterior. Vive aquí y no en una
  // pantalla aparte porque después de importar se revisa y se corrige en la misma
  // tabla de prenómina de siempre. Se pinta incluso sin cadencias: una empresa a media
  // migración puede no tener ninguna y seguir necesitando el importador.
  renderPieHistorica() {
    return (
      <div style={{ ...ZONA, marginTop: 24, borderTop: '1px dashed #d9d9d9', paddingTop: 16 }}>
        <div style={{ ...ETIQUETA_TENUE, marginBottom: 8 }}>
          ¿Migrando a media anualidad? Carga las nóminas que ya se pagaron en el sistema
          anterior para que los acumulados del ejercicio queden correctos. No se timbran.
        </div>
        <ButtonItem
          style={{ width: '100%' }}
          blanco
          icon={<HistoryOutlined />}
          onClick={() => this.props.onImportarHistorica()}
        >
          Cargar nómina histórica desde Excel
        </ButtonItem>
      </div>
    )
  }

  render() {
    const { cargando, errorCarga, periodos, ejercicios } = this.state
    const primeraCarga = cargando && periodos === null

    // Un solo envoltorio con el aire de arriba, para todas las ramas.
    let contenido
    if (primeraCarga) contenido = this.renderSkeleton()
    else if (errorCarga) contenido = this.renderError()
    else if (!ejercicios.length && !periodos?.length) contenido = this.renderVacio()
    else {
      contenido = (
        <>
          {this.renderSelector()}
          {this.renderRecientes()}
        </>
      )
    }

    return (
      <div style={ENVOLTURA}>
        {contenido}
        {!primeraCarga && this.renderPieHistorica()}
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  empresaId: state.empresa.id,
  headersToken: state.user.headersToken,
})

export default connect(mapStateToProps)(BandejaPrenomina)
